import type React from "react";
import { useRef, useState, useMemo, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import useElementRect from "../../../../../common/util/useElementRect";
import StatsNodeScene from "./StatsNodeScene";
import StatsNodeAsset from "./StatsNodeAsset";
import StatsLine from "./StatsLine";
import StatsTooltip from "./StatsTooltip";
import GraphLegend from "./GraphLegend";
import type { StatsNode, StatsNodeLink, GraphLayout } from "../../../types/graphStatsTypes";

interface StatsGraphProps {
  nodes: StatsNode[];
  links: StatsNodeLink[];
  layout: GraphLayout;
  isLoading?: boolean;
  error?: string;
  showNotVisited?: boolean;
  onSceneClick?: (node: StatsNode) => void;
  onInteractionClick?: (node: StatsNode) => void;
  /** Fixed height in pixels. If provided, uses fixed layout. If omitted, uses flex layout to fill parent. */
  height?: number;
  showLegend?: boolean;
  showControls?: boolean;
  /** Node contentId to focus/center on. When changed, the graph will pan and zoom to show this node. */
  focusNodeId?: number | null;
}

const StatsGraph: React.FC<StatsGraphProps> = ({
  nodes,
  links,
  layout,
  isLoading,
  error,
  showNotVisited = false,
  onSceneClick,
  onInteractionClick,
  height,
  showLegend = true,
  showControls = true,
  focusNodeId,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const sizeRect = useElementRect(containerRef);

  // Track pending focus that couldn't be applied due to missing sizeRect
  const pendingFocusRef = useRef<number | null>(null);

  // Scale limits
  const minScale = 1;
  const maxScale = 12;

  // Zoom and pan state (like SVGGraph)
  const [graphScale, setGraphScale] = useState(minScale);
  const graphScaleRef = useRef(graphScale);
  graphScaleRef.current = graphScale;
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [graphTranslateX, setGraphTranslateX] = useState(0);
  const [graphTranslateY, setGraphTranslateY] = useState(0);

  // Tooltip state
  const [hoveredNode, setHoveredNode] = useState<StatsNode | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Filter state: hide non-scored interactions
  const [hideScoredOnly, setHideScoredOnly] = useState(false);

  // Determine layout mode: fixed (height provided) or flex (no height)
  const useFlexLayout = height === undefined;

  // Calculate dimensions - use measured height from ref when in flex mode, or fixed height
  const containerHeight = useFlexLayout ? sizeRect?.height || 400 : height;

  // Add padding around the graph content
  const padding = 100;
  const graphWidth = layout.width + padding;
  const graphHeight = layout.height + padding;

  // ViewBox for the SVG - show entire graph content
  const viewBox = `0 0 ${graphWidth} ${graphHeight}`;

  // Content offset (half padding to center)
  const tx = padding / 2;
  const ty = padding / 2;

  // Determine which nodes should be dimmed (non-scored when filter is active)
  const dimmedNodeIds = useMemo(() => {
    if (!hideScoredOnly) return new Set<number>();
    return new Set(
      nodes
        .filter((node) => {
          if (node.type === "scene") {
            // Dim scenes without stats (no scored interactions inside)
            return node.stats == null;
          }
          // Dim non-scored interactions (not isScored AND no stats data)
          return node.isScored !== true && node.stats == null;
        })
        .map((n) => n.contentId),
    );
  }, [nodes, hideScoredOnly]);

  // Render node elements
  const nodeElements = useMemo(() => {
    return nodes.map((node) => {
      const isDimmed = dimmedNodeIds.has(node.contentId);
      if (node.type === "scene") {
        return (
          <StatsNodeScene
            key={`scene-${node.contentId}`}
            node={node}
            dimmed={isDimmed}
            onClick={isDimmed ? undefined : onSceneClick}
            onMouseEnter={
              isDimmed
                ? undefined
                : (n, e) => {
                    setHoveredNode(n);
                    const rect = containerRef.current?.getBoundingClientRect();
                    if (rect) {
                      setTooltipPosition({
                        x: e.clientX - rect.left,
                        y: e.clientY - rect.top,
                      });
                    }
                  }
            }
            onMouseLeave={isDimmed ? undefined : () => setHoveredNode(null)}
          />
        );
      } else {
        // Assets are clickable if they are scored OR have stats data
        const isClickable = !isDimmed && (node.isScored === true || node.stats != null);
        return (
          <StatsNodeAsset
            key={`asset-${node.contentId}`}
            node={node}
            dimmed={isDimmed}
            onClick={isClickable ? onInteractionClick : undefined}
            onMouseEnter={
              isDimmed
                ? undefined
                : (n, e) => {
                    setHoveredNode(n);
                    const rect = containerRef.current?.getBoundingClientRect();
                    if (rect) {
                      setTooltipPosition({
                        x: e.clientX - rect.left,
                        y: e.clientY - rect.top,
                      });
                    }
                  }
            }
            onMouseLeave={isDimmed ? undefined : () => setHoveredNode(null)}
          />
        );
      }
    });
  }, [nodes, dimmedNodeIds, onSceneClick, onInteractionClick]);

  // Render link elements
  const linkElements = useMemo(() => {
    return links.map((link, index) => {
      // Dim link if either end is dimmed
      const isDimmed =
        dimmedNodeIds.has(link.from.contentId) || dimmedNodeIds.has(link.to.contentId);
      return <StatsLine key={`link-${index}`} link={link} dimmed={isDimmed} />;
    });
  }, [links, dimmedNodeIds]);

  // Translate setter with bounds (like SVGGraph)
  // Limits pan to keep the graph content visible
  const setTranslate = useCallback(
    (newGraphTranslateX: number, newGraphTranslateY: number) => {
      if (!sizeRect) {
        setGraphTranslateX(newGraphTranslateX);
        setGraphTranslateY(newGraphTranslateY);
        return;
      }

      // Simple bounds like SVGGraph: limit to half the container size
      // This keeps the graph center always within the visible area
      const w = sizeRect.width / 2;
      const h = sizeRect.height / 2;

      // Clamp X
      if (newGraphTranslateX > w) {
        setGraphTranslateX(w);
      } else if (newGraphTranslateX < -w) {
        setGraphTranslateX(-w);
      } else {
        setGraphTranslateX(newGraphTranslateX);
      }

      // Clamp Y
      if (newGraphTranslateY > h) {
        setGraphTranslateY(h);
      } else if (newGraphTranslateY < -h) {
        setGraphTranslateY(-h);
      } else {
        setGraphTranslateY(newGraphTranslateY);
      }
    },
    [sizeRect],
  );

  // Scale setter with bounds (like SVGGraph)
  const setScale = useCallback(
    (newScale: number) => {
      if (newScale > maxScale) setGraphScale(maxScale);
      else if (newScale < minScale) setGraphScale(minScale);
      else setGraphScale(newScale);

      // Re-apply translate limits after scale change
      setTranslate(graphTranslateX, graphTranslateY);
    },
    [setTranslate, graphTranslateX, graphTranslateY],
  );

  // Detect Mac for modifier key hint
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform);
  const modifierKey = isMac ? "Cmd" : "Alt";

  // Wheel handler - only zoom when Cmd (Mac) or Alt (Windows/Linux) is pressed
  const handleWheel = useCallback(
    (event: WheelEvent) => {
      // Only zoom if Cmd (Mac) or Alt (Windows/Linux) is pressed
      if (event.metaKey || event.altKey) {
        event.preventDefault();
        event.stopPropagation();
        const currentScale = graphScaleRef.current;
        const newScale = currentScale + event.deltaY * -0.01;
        setScale(newScale);
      }
    },
    [setScale],
  );

  // Attach native wheel event listener with { passive: false }
  // Include nodes.length to re-attach when content becomes available after loading
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [handleWheel, nodes.length]);

  // Store pending focus when sizeRect is not available yet
  useEffect(() => {
    if (focusNodeId != null) {
      pendingFocusRef.current = focusNodeId;
    }
  }, [focusNodeId]);

  // Apply focus when all conditions are met (including pending focus)
  useEffect(() => {
    // Determine which focusNodeId to use: current prop or pending
    const effectiveFocusId = focusNodeId ?? pendingFocusRef.current;

    if (effectiveFocusId == null || nodes.length === 0 || !sizeRect) {
      return;
    }

    // Find the node to focus on (compare as numbers to handle string/number mismatch)
    const targetNode = nodes.find((n) => n.contentId === Number(effectiveFocusId));

    if (!targetNode) {
      return;
    }

    // Get container dimensions
    const containerWidth = sizeRect.width;
    const containerHeight = sizeRect.height;
    if (!containerWidth || !containerHeight) {
      return;
    }

    // Clear pending focus since we're applying it now
    pendingFocusRef.current = null;

    // Node position in viewBox coordinates (with padding offset)
    const nodeViewBoxX = targetNode.x + tx;
    const nodeViewBoxY = targetNode.y + ty;

    // Center of viewBox
    const viewBoxCenterX = graphWidth / 2;
    const viewBoxCenterY = graphHeight / 2;

    // Offset from center in viewBox coordinates
    const offsetX = nodeViewBoxX - viewBoxCenterX;
    const offsetY = nodeViewBoxY - viewBoxCenterY;

    // Calculate the ratio between viewBox and container (preserveAspectRatio="xMidYMid meet")
    const viewBoxToContainerRatio = Math.min(
      containerWidth / graphWidth,
      containerHeight / graphHeight,
    );

    // Apply zoom and center on node
    const focusZoom = 4.5;
    const newTranslateX = -offsetX * viewBoxToContainerRatio;
    const newTranslateY = -offsetY * viewBoxToContainerRatio;

    setGraphScale(focusZoom);
    setGraphTranslateX(newTranslateX);
    setGraphTranslateY(newTranslateY);
  }, [focusNodeId, nodes, tx, ty, graphWidth, graphHeight, sizeRect]);

  // Mouse handlers
  const handleMouseDown = () => setIsMouseDown(true);
  const handleMouseUp = () => setIsMouseDown(false);
  const handleMouseLeave = () => {
    setIsMouseDown(false);
    setHoveredNode(null);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    // Update tooltip position when moving over the graph
    if (hoveredNode) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        setTooltipPosition({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }
    }

    // Pan when dragging (like SVGGraph)
    if (isMouseDown) {
      const factor = 2;
      const newGraphTranslateX = graphTranslateX + (e.movementX * factor) / graphScale;
      const newGraphTranslateY = graphTranslateY + (e.movementY * factor) / graphScale;

      setTranslate(newGraphTranslateX, newGraphTranslateY);
    }
  };

  const resetView = () => {
    setGraphScale(minScale);
    setGraphTranslateX(0);
    setGraphTranslateY(0);
  };

  const zoomButtonStep = maxScale / 10.0;
  const zoomIn = () => setScale(graphScale + zoomButtonStep);
  const zoomOut = () => setScale(graphScale - zoomButtonStep);

  // Determine what to show in the graph container
  const showLoading = isLoading;
  const showError = !isLoading && error;
  const showEmpty = !isLoading && !error && nodes.length === 0;
  const showGraph = !isLoading && !error && nodes.length > 0;

  return (
    <div className={useFlexLayout ? "flex flex-col h-full gap-2" : "space-y-4"}>
      {/* Controls - fixed height, only show when graph is visible */}
      {showControls && showGraph && (
        <div
          className={`flex items-center justify-center gap-2 ${
            useFlexLayout ? "flex-shrink-0 pt-2" : "mt-4"
          }`}
        >
          <button
            onClick={resetView}
            className="px-3 py-1.5 text-sm font-medium bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            {t("statsNew.graph.resetZoom")}
          </button>
          <button
            onClick={zoomOut}
            disabled={graphScale <= minScale}
            className="p-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"
              />
            </svg>
          </button>
          <button
            onClick={zoomIn}
            disabled={graphScale >= maxScale}
            className="p-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"
              />
            </svg>
          </button>
          <div className="h-5 w-px bg-gray-300 mx-1" />
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              onClick={() => setHideScoredOnly(false)}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                !hideScoredOnly
                  ? "bg-green-500 text-white"
                  : "bg-white text-gray-500 hover:bg-gray-50"
              }`}
            >
              {t("statsNew.graph.showAll")}
            </button>
            <button
              onClick={() => setHideScoredOnly(true)}
              className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-gray-300 ${
                hideScoredOnly
                  ? "bg-green-500 text-white"
                  : "bg-white text-gray-500 hover:bg-gray-50"
              }`}
            >
              {t("statsNew.graph.scoredOnly")}
            </button>
          </div>
        </div>
      )}

      {/* Graph Container - ALWAYS rendered so useElementRect can measure it */}
      <div
        ref={containerRef}
        className={`relative bg-gray-100 rounded-xl overflow-hidden ${
          showGraph ? "cursor-grab active:cursor-grabbing" : ""
        } select-none ${useFlexLayout ? "flex-1 min-h-0" : ""}`}
        style={useFlexLayout ? undefined : { height }}
        onMouseDown={showGraph ? handleMouseDown : undefined}
        onMouseUp={showGraph ? handleMouseUp : undefined}
        onMouseMove={showGraph ? handleMouseMove : undefined}
        onMouseLeave={showGraph ? handleMouseLeave : undefined}
      >
        {/* Loading state */}
        {showLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
          </div>
        )}

        {/* Error state */}
        {showError && (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-red-500 text-sm">{error}</p>
          </div>
        )}

        {/* Empty state */}
        {showEmpty && (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-gray-500 text-sm">{t("statsNew.graph.noNodes")}</p>
          </div>
        )}

        {/* Graph content */}
        {showGraph && (
          <>
            {/* Scaled wrapper (like SVGGraph) */}
            <div
              className={useFlexLayout ? "h-full" : undefined}
              style={{
                transform: `scale(${graphScale}) translate(${graphTranslateX}px, ${graphTranslateY}px)`,
                transformOrigin: "center center",
              }}
            >
              <svg
                width="100%"
                height={useFlexLayout ? "100%" : containerHeight}
                viewBox={viewBox}
                preserveAspectRatio="xMidYMid meet"
                style={{ backgroundColor: "inherit" }}
                alignmentBaseline="middle"
                textAnchor="middle"
              >
                <g transform={`translate(${tx} ${ty})`}>
                  {linkElements}
                  {nodeElements}
                </g>
              </svg>
            </div>

            {/* Tooltip */}
            <StatsTooltip
              node={hoveredNode}
              x={tooltipPosition.x}
              y={tooltipPosition.y}
              containerRect={sizeRect}
            />

            {/* Zoom hint overlay */}
            <div className="absolute top-3 right-3 flex items-center gap-2 bg-black/60 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full pointer-events-none">
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"
                />
              </svg>
              <span>
                {modifierKey} + {t("statsNew.graph.scrollToZoom")}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Legend */}
      {showLegend && showGraph && (
        <div
          className={`flex justify-center ${useFlexLayout ? "flex-shrink-0 py-2" : "mt-2 pb-4"}`}
        >
          <GraphLegend showNotVisited={showNotVisited} />
        </div>
      )}
    </div>
  );
};

export default StatsGraph;
